import { Box, Container, Typography, Link } from "@mui/material";
import theme from "../../../theme/Theme";
import fairTransparentImg from "../../../assets/images/articlesimg/Fair&Transparent_article.png";

const FairTransparentArticle = () => {
  return (
    <Box sx={{ mb: 4 }}>
      <Container sx={styles.container}>
        <Typography variant="h1" sx={styles.title}>
          100% Fair and Transparent Lottery: How Blockchain Ensures Integrity in
          Digital Draws
        </Typography>
        <Box
          component="img"
          src={fairTransparentImg}
          alt="Fair & Transparent Lottery"
          sx={styles.heroImage}
        />
        <Box sx={styles.contentBox}>
          <Typography sx={styles.sectionText}>
            Traditional lotteries and prize draws have been popular for
            generations, offering hope and excitement through the simple
            purchase of a ticket. However, traditional systems often struggle
            with transparency issues, raising concerns about fairness and
            credibility among participants. <strong>SWSS Draw</strong> is
            pioneering a revolutionary change, leveraging blockchain technology
            to offer a genuinely transparent and fair lottery system.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Understanding the Drawbacks of Traditional Lotteries
          </Typography>
          <Typography sx={styles.sectionText}>
            Conventional lottery systems operate under centralized control,
            where decisions about winners and prize distributions are handled
            internally, often behind closed doors. This lack of transparency can
            lead to skepticism, mistrust, and accusations of rigging or
            manipulation. Participants typically have no reliable way to verify
            the fairness of the process, creating an environment of uncertainty.
          </Typography>
          <Typography sx={styles.sectionText}>
            <strong>SWSS Draw</strong> addresses these issues directly,
            eliminating any room for doubt by harnessing blockchain technology—a
            decentralized, public ledger system that ensures all actions are
            transparent, verifiable, and immutable.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            How Blockchain Technology Achieves Complete Transparency
          </Typography>
          <Typography sx={styles.sectionText}>
            Blockchain technology fundamentally changes the game by providing a
            tamper-proof, publicly accessible record of every transaction. Every
            ticket purchased, every prize distributed, and every winner selected
            is securely and transparently recorded on the blockchain.
          </Typography>
          <Typography sx={styles.sectionText}>
            Here's exactly how <strong>SWSS Draw</strong> ensures complete
            fairness and transparency:
          </Typography>

          <Box component="ul" sx={styles.orderedList}>
            <li>
              <strong>Smart Contract Automation</strong>
              <br />
              The entire lottery operation on SWSS Draw is automated using smart
              contracts—self-executing contracts with the terms of the agreement
              directly written into code. Smart contracts run on blockchain
              networks, and once set, they cannot be altered or tampered with.
              This means that all the rules and prize distributions are
              predefined, transparent, and completely secure from human
              interference or bias.
            </li>
            <li>
              <strong>Immutable Records</strong>
              <br />
              Every ticket issued is permanently minted as a unique Non-Fungible
              Token (NFT). NFTs are distinct digital assets that represent
              verifiable ownership on the blockchain. Each ticket NFT includes
              details such as the purchaser's wallet address, ticket number, and
              timestamp. This information is permanently recorded, ensuring that
              participants have a verifiable proof of participation that cannot
              be disputed.
            </li>
            <li>
              <strong>Verifiable Winner Selection</strong>
              <br />
              One of the key innovations in SWSS Draw is the use of an on-chain
              randomization process to select winners. Unlike traditional
              systems, where winners may be chosen through opaque or
              questionable methods, SWSS Draw's blockchain-based approach
              guarantees that winner selection is truly random, auditable, and
              entirely free from manipulation.
              <br />
              Participants and external auditors alike can independently verify
              the randomization algorithm on the blockchain, providing absolute
              assurance that the selection process is unbiased and fair.
            </li>
          </Box>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Ensuring Accessibility and Flexibility
          </Typography>
          <Typography sx={styles.sectionText}>
            Transparency is not the only advantage. SWSS Draw ensures
            inclusivity and flexibility by allowing users worldwide to
            participate using a variety of cryptocurrencies and tokens.
            Participants only need a crypto wallet to buy tickets or receive
            prizes, democratizing access to a global audience.
          </Typography>
          <Typography sx={styles.sectionText}>
            Moreover, SWSS Draw goes beyond traditional lotteries by offering
            multiple prize levels, meaning more participants have the chance to
            win. This approach enhances engagement and fosters trust, as more
            people directly experience the fairness and transparency of the
            system.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Empowering the Community Through Referrals
          </Typography>
          <Typography sx={styles.sectionText}>
            Another innovative aspect of SWSS Draw is its multi-level referral
            program. Unlike conventional referral schemes, the SWSS Draw
            referral program is transparently tracked on the blockchain. Every
            referral transaction is recorded, allowing participants to earn
            commissions seamlessly and transparently every time someone
            purchases a ticket using their referral link.
          </Typography>
          <Typography sx={styles.sectionText}>
            This transparent approach ensures trust and encourages active
            community participation, further amplifying the benefits and reach
            of the lottery.
          </Typography>
          <Typography sx={styles.sectionText}>
            You can learn more about the referral program and its benefits by
            visiting SWSS Draw's{" "}
            <Link
              href="/referral-earning"
              sx={{ color: theme.palette.primary.main }}
            >
              Referral Earning page
            </Link>
            .
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Real-world Verification and Trust
          </Typography>
          <Typography sx={styles.sectionText}>
            The SWSS Draw blockchain-powered lottery platform exemplifies the
            best of blockchain capabilities in action, aligning closely with
            leading blockchain lottery models noted for their transparent and
            fair methodologies. For instance, similar blockchain lotteries have
            been extensively reviewed and validated for their transparency and
            fairness by reputable sources like Blockchain Council, reinforcing
            SWSS Draw's credibility and the validity of blockchain lottery
            concepts.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Trust Through Transparency: The Future of Lotteries
          </Typography>
          <Typography sx={styles.sectionText}>
            The transparency and fairness provided by blockchain technology are
            transforming how people perceive and participate in lotteries. SWSS
            Draw's commitment to an entirely transparent process not only
            addresses traditional lottery shortcomings but also sets a new
            benchmark for digital competitions.
          </Typography>
          <Typography sx={styles.sectionText}>
            Participants are assured of their fair chance at winning, creators
            are empowered to launch their draws securely, and communities can
            engage confidently knowing that each step of the lottery is visible
            and verifiable.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Join the Revolution Today
          </Typography>
          <Typography sx={styles.sectionText}>
            SWSS Draw represents more than just a digital lottery—it's a
            revolutionary step forward in digital competitions, built on trust,
            transparency, and fairness. Every ticket purchase, every draw
            conducted, and every winner announced reinforces the integrity of
            blockchain technology.
          </Typography>
          <Typography sx={styles.sectionText}>
            Discover this groundbreaking approach to fair play yourself by
            exploring{" "}
            <Link href="/" sx={{ color: theme.palette.primary.main }}>
              SWSS Draw's official platform
            </Link>{" "}
            and experience firsthand why blockchain-powered lotteries are the
            future.
          </Typography>

          <Typography variant="h2" sx={styles.sectionTitle}>
            Conclusion
          </Typography>
          <Typography sx={styles.sectionText}>
            SWSS Draw provides a secure, transparent, and completely fair
            lottery environment powered by blockchain technology. Participants
            can confidently engage in draws knowing that every process is
            publicly recorded, verifiable, and tamper-proof. Embracing
            blockchain transparency, SWSS Draw ensures that lotteries remain
            exciting, trustworthy, and genuinely fair for everyone involved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

const styles = {
  container: {
    py: 4,
    maxWidth: "1200px",
  },
  title: {
    fontSize: { xs: "1.8rem", md: "2.5rem" },
    fontWeight: 700,
    color: theme.palette.text.primary,
    mb: 3,
    textAlign: "center",
    fontFamily: '"Proxima Nova", sans-serif',
  },
  heroImage: {
    width: "100%",
    position: "relative",
    aspectRatio: { xs: "16 / 9", md: "17 / 9" }, // or whatever ratio fits best
    mb: 4,
    borderRadius: 2,
    overflow: "hidden",
  },
  contentBox: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    boxShadow: 1,
    p: { xs: 2, md: 4 },
  },
  sectionTitle: {
    fontSize: { xs: "1.5rem", md: "1.8rem" },
    fontWeight: 600,
    color: "black",
    fontFamily: '"Proxima Nova", sans-serif',
    mt: 3,
    mb: 3,
  },
  sectionText: {
    fontSize: { xs: "0.9rem", md: "1.2rem" },
    fontFamily: '"Proxima Nova", sans-serif',
    fontWeight: 400,
    lineHeight: 1.6,
    mb: 2,
    color: "black",
  },
  orderedList: {
    pl: 3,
    mb: 2,
    "& li": {
      mb: 1,
      fontSize: { xs: "0.9rem", md: "1.1rem" },
      fontFamily: '"Proxima Nova", sans-serif',
    },
    listStyleType: "decimal !important",
  },
};

export default FairTransparentArticle;
